import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Boxes, Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import { formatBRL } from "@/lib/format";

const FEATURE_KEYS = [
  { key: "ai", label: "IA" },
  { key: "appcc", label: "APPCC" },
  { key: "audits", label: "Auditorias" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "offline", label: "Offline" },
  { key: "multi_unit", label: "Multi-unidade" },
  { key: "stock_control", label: "Controle de Estoque" },
];

const LIMIT_LABELS = { users: "Usuários", units: "Unidades", checklists: "Checklists", storage_mb: "Storage (MB)" };

const EMPTY = {
  name: "",
  slug: "",
  monthly_price: 0,
  yearly_price: 0,
  description: "",
  limits: { users: 5, units: 1, checklists: 10, storage_mb: 1024 },
  features: { ai: false, appcc: false, audits: false, whatsapp: false, offline: false, multi_unit: false, stock_control: true },
};

const toForm = (p) => ({
  name: p.name,
  slug: p.slug,
  monthly_price: p.monthly_price,
  yearly_price: p.yearly_price,
  description: p.description || "",
  limits: { ...EMPTY.limits, ...(p.limits || {}) },
  features: { ...EMPTY.features, ...(p.features || {}) },
});

function PlanForm({ form, setForm, onSubmit, editing }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="plan-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Nome *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 bg-white" data-testid="plan-name" />
        </div>
        <div>
          <Label>Slug *</Label>
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required className="mt-1 bg-white" data-testid="plan-slug" />
        </div>
        <div>
          <Label>Mensal (R$)</Label>
          <Input type="number" min="0" step="0.01" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: Number(e.target.value) })} className="mt-1 bg-white" data-testid="plan-monthly" />
        </div>
        <div>
          <Label>Anual (R$)</Label>
          <Input type="number" min="0" step="0.01" value={form.yearly_price} onChange={(e) => setForm({ ...form, yearly_price: Number(e.target.value) })} className="mt-1 bg-white" data-testid="plan-yearly" />
        </div>
        <div className="col-span-2">
          <Label>Descrição</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white" rows={2} />
        </div>
      </div>

      <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Limites</div>
      <div className="grid grid-cols-2 gap-3">
        {Object.keys(form.limits).map((k) => (
          <div key={k}>
            <Label>{LIMIT_LABELS[k] || k}</Label>
            <Input
              type="number"
              min="0"
              value={form.limits[k]}
              onChange={(e) => setForm({ ...form, limits: { ...form.limits, [k]: Number(e.target.value) } })}
              className="mt-1 bg-white"
              data-testid={`plan-limit-${k}`}
            />
          </div>
        ))}
      </div>

      <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Recursos</div>
      <div className="grid grid-cols-2 gap-3">
        {FEATURE_KEYS.map((f) => (
          <label key={f.key} className="flex items-center justify-between text-sm font-body border border-stone-200 rounded-md px-3 py-2">
            <span>{f.label}</span>
            <Switch
              checked={!!form.features[f.key]}
              onCheckedChange={(v) => setForm({ ...form, features: { ...form.features, [f.key]: v } })}
              data-testid={`plan-feature-${f.key}`}
            />
          </label>
        ))}
      </div>

      <DialogFooter className="pt-2">
        <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid={TID.planSubmit}>
          {editing ? "Salvar alterações" : "Criar plano"}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminPlans() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setItems(data);
    } catch (err) {
      console.error("Failed to load admin plans", err);
    }
  };

  const activeFeaturesByPlan = useMemo(() => {
    const map = {};
    for (const p of items) {
      map[p.id] = FEATURE_KEYS.filter((k) => p.features?.[k.key]);
    }
    return map;
  }, [items]);

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm(toForm(p));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/plans/${editing.id}`, form);
        toast.success("Plano atualizado");
      } else {
        await api.post("/admin/plans", form);
        toast.success("Plano criado");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editing ? "Falha ao atualizar" : "Falha ao criar"));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/admin/plans/${deleting.id}`);
      toast.success(`Plano "${deleting.name}" excluído`);
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir plano", { duration: 8000 });
    }
  };

  return (
    <Layout
      title="Planos"
      subtitle="Defina os planos comerciais oferecidos"
      actions={
        <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" onClick={openCreate} data-testid={TID.planNew}>
          <Plus className="h-4 w-4 mr-2" /> Novo plano
        </Button>
      }
    >
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-xl" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Editar plano · ${editing.name}` : "Novo plano"}</DialogTitle>
          </DialogHeader>
          <PlanForm form={form} setForm={setForm} onSubmit={submit} editing={!!editing} />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="plan-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Excluir plano "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              A exclusão é permanente. Planos em uso por empresas ou assinaturas ativas não podem ser excluídos — migre as empresas antes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plan-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="plan-delete-confirm">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div data-testid={TID.adminPlans} className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.length === 0 && (
          <Card className="md:col-span-4 p-10 text-center text-stone-500 font-body">
            <Boxes className="h-6 w-6 mx-auto mb-2" /> Nenhum plano cadastrado
          </Card>
        )}
        {items.map((p) => (
          <Card key={p.id} className="p-6 rounded-xl border-stone-200 bg-white flex flex-col" data-testid={`plan-card-${p.slug}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display text-xl font-bold tracking-tight truncate">{p.name}</div>
                <div className="text-xs text-stone-500 font-body">/{p.slug}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-500 hover:text-emerald-800 hover:bg-emerald-50" onClick={() => openEdit(p)} aria-label={`Editar ${p.name}`} data-testid={`plan-edit-${p.slug}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-500 hover:text-rose-700 hover:bg-rose-50" onClick={() => setDeleting(p)} aria-label={`Excluir ${p.name}`} data-testid={`plan-delete-${p.slug}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="mt-4 font-display text-3xl font-extrabold">
              {formatBRL(p.monthly_price)}
              <span className="text-xs text-stone-500 font-body font-medium ml-1">/mês</span>
            </div>
            <div className="text-xs text-stone-500 font-body">{formatBRL(p.yearly_price)} /ano</div>
            <div className="mt-4 text-sm font-body text-stone-700 space-y-1">
              <div>Usuários: <span className="font-semibold">{p.limits?.users}</span></div>
              <div>Unidades: <span className="font-semibold">{p.limits?.units}</span></div>
              <div>Checklists: <span className="font-semibold">{p.limits?.checklists}</span></div>
              <div>Storage: <span className="font-semibold">{Math.round((p.limits?.storage_mb || 0) / 1024)} GB</span></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1">
              {(activeFeaturesByPlan[p.id] || []).map((k) => (
                <Badge key={k.key} className="bg-emerald-700 text-white">{k.label}</Badge>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </Layout>
  );
}
